import { renderSettings } from "@shared/RenderSettings.js";
import { Product } from "@shared/Product.js";
import { Rect } from "@shared/Rect.js";
import { RandomState } from "@shared/RandomState.js";
import { dlog, ArrayUtil, debugDesc } from "@shared/utility.js";

const Edge = {
    top: "top",
    right: "right",
    bottom: "bottom",
    left: "left",
    center: "center",

    // Which corner location is "primary" for the given product (i.e. where should the upload image get drawn, if any). (Now unused?)
    isPrimaryCornerForDevice(device) {
        switch (device.productCategory) {
            case "case":
                return [Edge.top, Edge.right]
            case "grip":
                return [Edge.bottom, Edge.right]
            case "wallet":
                return [Edge.top, Edge.right]
        }
    },
    topLeftCorner() {
        return [Edge.top, Edge.left]
    },
    topRightCorner() {
        return [Edge.top, Edge.right]
    },
    bottomLeftCorner() {
        return [Edge.bottom, Edge.left]
    },
    bottomRightCorner() {
        return [Edge.bottom, Edge.right]
    },

}

export class BorderLoc {
    constructor(x, y, edges /* [Edge] */ ) {
        this.x = x
        this.y = y
        this.edges = edges
    }

    hasEdge(edge) {
        return this.edges.includes(edge)
    }

    hasEdges(edges) {
        return arraysUnorderedEqual(this.edges, edges)
    }

    hasAnyEdge(edges) {
        for (const edge of edges) {
            if (this.hasEdge(edge)) {
                return true
            }
        }
        return false
    }

    isEdge(edge) {
        return arraysUnorderedEqual(this.edges, [edge])
    }

    // Currently we want the top middle position on the case to be considered the "corner"
    isCorner() {
        return arraysUnorderedEqual(this.edges, Edge.topLeftCorner()) ||
            arraysUnorderedEqual(this.edges, Edge.topRightCorner()) ||
            arraysUnorderedEqual(this.edges, Edge.bottomLeftCorner()) ||
            arraysUnorderedEqual(this.edges, Edge.bottomRightCorner())
    }

    isPrimaryForDevice(device) {
        return Edge.isPrimaryCornerForDevice(device)
    }

    pt() {
        return [this.x, this.y]
    }

    ptX() {
        return this.x;
    }

    ptY() {
        return this.y;
    }

    // Return an array of BorderLocs that specifies where stickers should be drawn along the border.
    // Points represent the top left origin of the sticker.
    // For case devices:
    // - stickerHeight provides the height of stickers to properly calculate spacing and prevent overlap
    static getLocs(device, canvasWidth, canvasHeight, stickerHeight) {
        // get the total length around the border the images can span
        let x, y, x0, y0;
        var borderLocs = [];

        // Get edge insets for this device
        const insetRatios = device.safeAreaInsets;
        const topInsetRatio = ArrayUtil.top(insetRatios);
        const rightInsetRatio = ArrayUtil.right(insetRatios);
        const bottomInsetRatio = ArrayUtil.bottom(insetRatios);
        const leftInsetRatio = ArrayUtil.left(insetRatios);
        
        let cameraCutoutFrame;
        if (device.cameraCutoutFrame) {
            cameraCutoutFrame = Rect.fromArray(device.cameraCutoutFrame);
        }
        
        const cameraHInsetRatio = cameraCutoutFrame ? cameraCutoutFrame.maxX : 0;
        const cameraYInsetRatio = cameraCutoutFrame ? cameraCutoutFrame.maxY : 0;

        const borderMarginRatios = device.borderStickerMargin;

        const borderMarginTop = ArrayUtil.top(borderMarginRatios) * canvasHeight;
        const borderMarginRight = ArrayUtil.right(borderMarginRatios) * canvasWidth;
        const borderMarginBottom = ArrayUtil.bottom(borderMarginRatios) * canvasHeight;
        const borderMarginLeft = ArrayUtil.left(borderMarginRatios) * canvasWidth;
        
        let numIntermediateStickers;
        
        if (device.productCategory == Product.wallet) {
            if (stickerHeight <= 0) {
                throw new Error("Missing stickerHeight");
            }

            const topSideStickerCnt = device.sideStickerCnt ? device.sideStickerCnt[0] : 4;
            const rightSideStickerCnt = device.sideStickerCnt ? device.sideStickerCnt[1] : 5;
            const leftSideStickerCnt = device.sideStickerCnt ? device.sideStickerCnt[3] : 5;
            
            const borderRightYStartRatio = device.borderDrawStartOffsets 
                ? device.borderDrawStartOffsets[1]
                : 0;
            const broderRightYStart = borderRightYStartRatio * canvasHeight;

            // top left
            // SKIP: because this gets drawn again below for "top left"
            // x = canvasWidth * cameraHInsetRatio + borderMarginLeft;
            // y = canvasHeight * topInsetRatio + borderMarginTop;
            // borderLocs.push(new BorderLoc(x, y, [Edge.top, Edge.center]));
            // dlog(`topCenter: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            
            // top right - this will be used for top edge calculations as well
            x = canvasWidth * (1 - rightInsetRatio) - borderMarginRight;
            y = broderRightYStart;
            borderLocs.push(new BorderLoc(x, y, [Edge.top, Edge.right]));
            dlog(`topRight: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            
            // First draw top right corner (already drawn above)
            const topRightY = y;
            
            // Then draw bottom right corner
            const bottomRightY = canvasHeight * (1 - bottomInsetRatio) - borderMarginBottom;
            borderLocs.push(new BorderLoc(x, bottomRightY, [Edge.bottom, Edge.right]));
            dlog(`bottomRight: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            
            // Then draw intermediate stickers between the corners if needed
            // Calculate the available vertical space
            let availableHeight = bottomRightY - topRightY; // from top right to bottom right
            
            // Calculate spacing for additional stickers
            numIntermediateStickers = rightSideStickerCnt - 2; // minus the top and bottom corners
            
            // Calculate total space needed for all stickers including corners
            let totalStickerHeight = stickerHeight * rightSideStickerCnt;
            
            // Calculate remaining space for spacing between ALL stickers
            let remainingSpace = Math.max(0, availableHeight - totalStickerHeight);
            
            // Calculate spacing between stickers - need (rightSideStickerCnt - 1) spaces
            let spaceBetween = remainingSpace / (rightSideStickerCnt - 1);
            
            // Place right side intermediate stickers with even spacing, accounting for sticker height
            for (let i = 1; i < rightSideStickerCnt - 1; i++) {
                // Position calculation:
                // topRightY (position of first corner sticker)
                // + stickerHeight (height of first corner sticker)
                // + spaceBetween * i (spacing before this sticker - i spaces)
                // + stickerHeight * (i - 1) (height of stickers before this one)
                const stickerY = topRightY + stickerHeight + (spaceBetween * i) + (stickerHeight * (i - 1));
                borderLocs.push(new BorderLoc(x, stickerY, [Edge.right]));
                dlog(`right side evenly spaced with height ${i}: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            }
            
            // Set y to bottom position for subsequent code
            y = bottomRightY;
            
            // bottom middle
            // SKIP: because wallet can't draw stickers at bottom middle
            // x = canvasWidth / 2;
            // borderLocs.push(new BorderLoc(x, y, [Edge.bottom, Edge.center]));
            // dlog(`bottomMiddle: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            
            // bottom left
            x = leftInsetRatio * canvasWidth + borderMarginLeft;
            borderLocs.push(new BorderLoc(x, y, [Edge.bottom, Edge.left]));
            dlog(`bottomLeft: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            
            // top left
            const topLeftY = cameraYInsetRatio * canvasHeight + borderMarginTop;
            borderLocs.push(new BorderLoc(x, topLeftY, [Edge.top, Edge.left]));
            dlog(`topLeft: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            
            // Calculate spacing for stickers across the top
            // We already have top left and top right corners in our borderLocs array
            // so we'll just calculate positions for intermediate stickers
            const topLeftX = x; // we're already at top left x position
            const topRightX = canvasWidth * (1 - rightInsetRatio) - borderMarginRight;
            
            // Calculate the available horizontal space
            const availableWidth = topRightX - topLeftX;
            
            // Calculate spacing for additional stickers
            numIntermediateStickers = topSideStickerCnt - 2; // minus the top-left and top-right corners
            
            // Calculate total space needed for all stickers including corners
            // Using stickerHeight as width since they're square
            const totalStickerWidth = stickerHeight * topSideStickerCnt;
            
            // Calculate remaining space for spacing between ALL stickers
            const topRemainingSpace = Math.max(0, availableWidth - totalStickerWidth);
            
            // Calculate spacing between stickers - need (topSideStickerCnt - 1) spaces
            const topSpaceBetween = topRemainingSpace / (topSideStickerCnt - 1);
            
            // Place intermediate stickers with even spacing across the top
            // We only need to add the intermediate stickers (not corners)
            for (let i = 1; i < topSideStickerCnt - 1; i++) {
                // Position calculation:
                // topLeftX (position of first corner sticker)
                // + stickerHeight (width of first corner sticker)
                // + topSpaceBetween * i (spacing before this sticker - i spaces)
                // + stickerHeight * (i - 1) (width of stickers before this one)
                const stickerX = topLeftX + stickerHeight + (topSpaceBetween * i) + (stickerHeight * (i - 1));
                borderLocs.push(new BorderLoc(stickerX, topLeftY, [Edge.top]));
                dlog(`top side evenly spaced width ${i}: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            }
            
            // along left side with evenly spaced stickers
            // Calculate the available vertical space
            availableHeight = y - topLeftY; // from bottom left to top left
            
            // Calculate spacing for additional stickers
            numIntermediateStickers = leftSideStickerCnt - 2; // minus the top and bottom corners
                        
            // Need to account for all stickers including top and bottom corner stickers
            // Calculate total space needed for all stickers
            totalStickerHeight = stickerHeight * (numIntermediateStickers + 2); // +2 for corner stickers
            
            // Calculate remaining space for spacing between ALL stickers
            remainingSpace = Math.max(0, availableHeight - totalStickerHeight);
            
            // Calculate spacing between stickers - need (leftSideStickerCnt - 1) spaces
            spaceBetween = remainingSpace / (leftSideStickerCnt - 1);
            
            // Place intermediate stickers with even spacing, accounting for sticker height
            for (let i = 1; i <= numIntermediateStickers; i++) {
                // Position calculation:
                // topLeftY (position of first corner sticker)
                // + stickerHeight (height of first corner sticker)
                // + spaceBetween * i (spacing before this sticker - i spaces)
                // + stickerHeight * (i - 1) (height of stickers before this one)
                const stickerY = topLeftY + stickerHeight + (spaceBetween * i) + (stickerHeight * (i - 1));
                borderLocs.push(new BorderLoc(x, stickerY, [Edge.left]));
                dlog(`left side evenly spaced with height ${i}: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            }
        } else if (device.productCategory == Product.case) {
            if (stickerHeight <= 0) {
                throw new Error("Missing stickerHeight");
            }

            const rightSideStickerCnt = device.sideStickerCnt ? device.sideStickerCnt[1] : 5;
            const leftSideStickerCnt = device.sideStickerCnt ? device.sideStickerCnt[3] : 5;

            const borderRightYStartRatio = device.borderDrawStartOffsets 
                ? device.borderDrawStartOffsets[1]
                : 0.15;
            const broderRightYStart = borderRightYStartRatio * canvasHeight;

            // top center
            x = canvasWidth * cameraHInsetRatio + borderMarginLeft;
            y = canvasHeight * topInsetRatio + borderMarginTop;
            borderLocs.push(new BorderLoc(x, y, [Edge.top, Edge.center]));
            dlog(`topCenter: ${debugDesc(borderLocs[borderLocs.length - 1])}`);

            // top right
            x = canvasWidth * (1 - rightInsetRatio) - borderMarginRight;
            y = broderRightYStart;
            borderLocs.push(new BorderLoc(x, y, [Edge.top, Edge.right]));
            dlog(`topRight: ${debugDesc(borderLocs[borderLocs.length - 1])}`);

            // First draw top right corner (already drawn above)
            const topRightY = y;

            // Then draw bottom right corner
            const bottomRightY = canvasHeight * (1 - bottomInsetRatio) - borderMarginBottom;
            borderLocs.push(new BorderLoc(x, bottomRightY, [Edge.bottom, Edge.right]));
            dlog(`bottomRight: ${debugDesc(borderLocs[borderLocs.length - 1])}`);

            // Then draw intermediate stickers between the corners if needed
            // Calculate the available vertical space
            let availableHeight = bottomRightY - topRightY; // from top right to bottom right

            // Calculate spacing for additional stickers
            let numIntermediateStickers = rightSideStickerCnt - 2; // minus the top and bottom corners

            // Calculate total space needed for all stickers including corners
            let totalStickerHeight = stickerHeight * rightSideStickerCnt;

            // Calculate remaining space for spacing between ALL stickers
            let remainingSpace = Math.max(0, availableHeight - totalStickerHeight);

            // Calculate spacing between stickers - need (rightSideStickerCnt - 1) spaces
            let spaceBetween = remainingSpace / (rightSideStickerCnt - 1);

            // Place intermediate stickers with even spacing, accounting for sticker height
            for (let i = 1; i < rightSideStickerCnt - 1; i++) {
                // Position calculation:
                // topRightY (position of first corner sticker)
                // + stickerHeight (height of first corner sticker)
                // + spaceBetween * i (spacing before this sticker - i spaces)
                // + stickerHeight * (i - 1) (height of stickers before this one)
                const stickerY = topRightY + stickerHeight + (spaceBetween * i) + (stickerHeight * (i - 1));
                borderLocs.push(new BorderLoc(x, stickerY, [Edge.right]));
                dlog(`right side evenly spaced with height ${i}: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            }

            // Set y to bottom position for subsequent code
            y = bottomRightY;

            // bottom middle
            // this is center and not top/left because we don"t know the size of the img getting drawn. it needs to be adjusted in the caller
            x = canvasWidth / 2;
            borderLocs.push(new BorderLoc(x, y, [Edge.bottom, Edge.center]));
            dlog(`bottomMiddle: ${debugDesc(borderLocs[borderLocs.length - 1])}`);

            // bottom left
            x = leftInsetRatio * canvasWidth + borderMarginLeft;
            borderLocs.push(new BorderLoc(x, y, [Edge.bottom, Edge.left]));
            dlog(`bottomLeft: ${debugDesc(borderLocs[borderLocs.length - 1])}`);

            // top left
            const topLeftY = cameraYInsetRatio * canvasHeight + borderMarginTop;
            borderLocs.push(new BorderLoc(x, topLeftY, [Edge.top, Edge.left]));
            dlog(`topLeft: ${debugDesc(borderLocs[borderLocs.length - 1])}`);

            // along left side with evenly spaced stickers
            // Calculate the available vertical space
            availableHeight = y - topLeftY; // from bottom left to top left
            
            // Calculate spacing for additional stickers
            numIntermediateStickers = leftSideStickerCnt - 2; // minus the top and bottom corners
                        
            // Need to account for all stickers including top and bottom corner stickers
            // Calculate total space needed for all stickers
            totalStickerHeight = stickerHeight * (numIntermediateStickers + 2); // +2 for corner stickers
            
            // Calculate remaining space for spacing between ALL stickers
            remainingSpace = Math.max(0, availableHeight - totalStickerHeight);
            
            // Calculate spacing between stickers - need (leftSideStickerCnt - 1) spaces
            spaceBetween = remainingSpace / (leftSideStickerCnt - 1);
            
            // Place intermediate stickers with even spacing, accounting for sticker height
            for (let i = 1; i <= numIntermediateStickers; i++) {
                // Position calculation:
                // topLeftY (position of first corner sticker)
                // + stickerHeight (height of first corner sticker)
                // + spaceBetween * i (spacing before this sticker - i spaces)
                // + stickerHeight * (i - 1) (height of stickers before this one)
                const stickerY = topLeftY + stickerHeight + (spaceBetween * i) + (stickerHeight * (i - 1));
                borderLocs.push(new BorderLoc(x, stickerY, [Edge.left]));
                dlog(`left side evenly spaced with height ${i}: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
            }
        } else if (device.productCategory == Product.grip) {
            const borderSpacingTop = device.borderSpacingRatio * canvasWidth;

            x = canvasWidth / 2.0;
            y = canvasHeight / 2.0;
            x0 = x;
            y0 = y;
            const centerX = canvasWidth / 2.0;
            const centerY = canvasHeight / 2.0;
            
            // For grips the "top" margin is used uniformly around the circular border
            const borderMarginRatio = ArrayUtil.top(borderMarginRatios);
            
            // Apply border margins to the scale calculation
            const scale = 1 - (topInsetRatio * 2) - (borderMarginRatio * 2);
            const radius = (canvasWidth + canvasHeight) / 2 * scale / 2;

            const len = 2 * Math.PI * radius;
            // get number of stickers and spacing based on desired spacing
            const n = Math.max(Math.round(Math.abs(len) / borderSpacingTop), 1);
            const bs = len / (n - 1); // true border spacing, based on best number of stickers
            const angleOffsetDeg = 0; // jws: not sure what this was for?

            // Track "corners"
            const corners = {
                topRight: { index: -1, val: -Infinity },
                bottomRight: { index: -1, val: -Infinity },
                topLeft: { index: -1, val: -Infinity },
                bottomLeft: { index: -1, val: -Infinity }
            };

            var pts = [];

            for (let i = 0; i < (n - 1); i++) {
                const r = len / (2 * Math.PI);
                const theta = i * (bs / len) * 2 * Math.PI + angleOffsetDeg / 180.0 * Math.PI;
                y = y0 + r * Math.cos(theta);
                x = x0 + r * Math.sin(theta);

                pts.push([x, y]);

                if (x >= centerX) { // Right half
                    if (y <= centerY) { // Top half
                        // Top Right Corner
                        if (x * (canvasHeight - y) > corners.topRight.val) {
                            corners.topRight.val = x * y;
                            corners.topRight.index = i;
                        }
                    } else {
                        // Bottom Right Corner
                        if (x * y > corners.bottomRight.val) {
                            corners.bottomRight.val = x * y;
                            corners.bottomRight.index = i;
                        }
                    }
                } else { // Left half
                    if (y <= centerY) {
                        // Top Left Corner
                        if ((canvasWidth - x) * (canvasHeight - y) > corners.topLeft.val) {
                            corners.topLeft.val = x * y;
                            corners.topLeft.index = i;
                        }
                    } else {
                        // Bottom Left Corner
                        if ((canvasWidth - x) * y > corners.bottomLeft.val) {
                            corners.bottomLeft.val = x * y;
                            corners.bottomLeft.index = i;
                        }
                    }
                }
            }

            for (let i = 0; i < pts.length; i++) {
                const point = pts[i];

                if (i == corners.topRight.index) {
                    dlog(`topRight: ${ArrayUtil.x(point)}, ${ArrayUtil.y(point)}`)
                    borderLocs.push(new BorderLoc(ArrayUtil.x(point), ArrayUtil.y(point), [Edge.top, Edge.right]))
                } else if (i == corners.bottomRight.index) {
                    dlog(`bottomRight: ${ArrayUtil.x(point)}, ${ArrayUtil.y(point)}`)
                    borderLocs.push(new BorderLoc(ArrayUtil.x(point), ArrayUtil.y(point), [Edge.bottom, Edge.right]))
                } else if (i == corners.topLeft.index) {
                    dlog(`topLeft: ${ArrayUtil.x(point)}, ${ArrayUtil.y(point)}`)
                    borderLocs.push(new BorderLoc(ArrayUtil.x(point), ArrayUtil.y(point), [Edge.top, Edge.left]))
                } else if (i == corners.bottomLeft.index) {
                    dlog(`bottomLeft: ${ArrayUtil.x(point)}, ${ArrayUtil.y(point)}`)
                    borderLocs.push(new BorderLoc(ArrayUtil.x(point), ArrayUtil.y(point), [Edge.bottom, Edge.left]))
                } else {
                    dlog(`not a corner: ${ArrayUtil.x(point)}, ${ArrayUtil.y(point)}`)
                    borderLocs.push(new BorderLoc(ArrayUtil.x(point), ArrayUtil.y(point), []))
                }
            }
        } else {
            dlog("UNKNOWN PRODUCT: ", device.productCategory);
            return;
        }

        return borderLocs;
    }

    // ignoreMargins is for grip only and should be true when the image has a flat bottom.
    static getCornerLocs(device, canvasWidth, canvasHeight, ignoreMargins = false) {
        let x, y;
        var borderLocs = [];
    
        const insetRatios = device.safeAreaInsets;
        const topInsetRatio = ArrayUtil.top(insetRatios);
        const rightInsetRatio = ArrayUtil.right(insetRatios);
        const bottomInsetRatio = ArrayUtil.bottom(insetRatios);
        const leftInsetRatio = ArrayUtil.left(insetRatios);
                
        const borderMarginRatios = device.borderStickerMargin;
    
        const borderMarginTop = ArrayUtil.top(borderMarginRatios) * canvasHeight;
        const borderMarginRight = ArrayUtil.right(borderMarginRatios) * canvasWidth;
        const borderMarginBottom = ArrayUtil.bottom(borderMarginRatios) * canvasHeight;
        const borderMarginLeft = ArrayUtil.left(borderMarginRatios) * canvasWidth;
        
        if (device.productCategory == Product.case || device.productCategory == Product.wallet) {
            // top left
            x = canvasWidth * leftInsetRatio + borderMarginLeft;
            y = canvasHeight * topInsetRatio + borderMarginTop;
            borderLocs.push(new BorderLoc(x, y, [Edge.top, Edge.left]));
            dlog(`topLeft: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
        
            // top right
            x = canvasWidth * (1 - rightInsetRatio) - borderMarginRight;
            borderLocs.push(new BorderLoc(x, y, [Edge.top, Edge.right]));
            dlog(`topRight: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
        
            // bottom right
            y = canvasHeight * (1 - bottomInsetRatio) - borderMarginBottom;
            borderLocs.push(new BorderLoc(x, y, [Edge.bottom, Edge.right]));
            dlog(`bottomRight: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
    
            // bottom left
            x = canvasWidth * leftInsetRatio + borderMarginLeft;
            borderLocs.push(new BorderLoc(x, y, [Edge.bottom, Edge.left]));
            dlog(`bottomLeft: ${debugDesc(borderLocs[borderLocs.length - 1])}`);
        } else if (device.productCategory == Product.grip) {
            const borderMarginRatio = ignoreMargins ? 0 : ArrayUtil.top(borderMarginRatios);

            const scale = 1 - (topInsetRatio * 2) - (borderMarginRatio * 2);
            const fullRadius = canvasWidth / 2;
            const insetRadius = canvasWidth / 2 * scale;
            
            const rDiff = fullRadius - insetRadius;
            
            function getPoint(radius, angleDegrees) {
                let angleRadians = (angleDegrees - 90) * Math.PI / 180;
                let x = radius + radius * Math.cos(angleRadians) + rDiff;
                let y = radius + radius * Math.sin(angleRadians) + rDiff;
                return [x, y];
            }
            
            // Get corner points of the inset circle with radius
            var topRight = getPoint(insetRadius, 45);
            var bottomRight = getPoint(insetRadius, 135);
            var bottomLeft = getPoint(insetRadius, 225);
            var topLeft = getPoint(insetRadius, 315);
            
            var borderLocs = [];
            borderLocs.push(new BorderLoc(ArrayUtil.x(topRight), ArrayUtil.y(topRight), [Edge.top, Edge.right]));
            borderLocs.push(new BorderLoc(ArrayUtil.x(bottomRight), ArrayUtil.y(bottomRight), [Edge.bottom, Edge.right]));
            borderLocs.push(new BorderLoc(ArrayUtil.x(bottomLeft), ArrayUtil.y(bottomLeft), [Edge.bottom, Edge.left]));
            borderLocs.push(new BorderLoc(ArrayUtil.x(topLeft), ArrayUtil.y(topLeft), [Edge.top, Edge.left]));
        } else {
            dlog("UNKNOWN PRODUCT: ", device.productCategory);
            return;
        }

        return borderLocs;
    }

    // Returns a random corner loc from locs, optionally avoiding the given edgesToAvoid
    // Include `stateKey` to make this toggle each time for that key.
    static randomBottomCornerLoc(locs, edgesToAvoid = null, stateKey, randomState = null) {
        const bottomLeftLoc = BorderLoc.firstLocWithEdges(locs, [Edge.bottom, Edge.left])
        const bottomRightLoc = BorderLoc.firstLocWithEdges(locs, [Edge.bottom, Edge.right])

        if (edgesToAvoid) {
            // not really random, just return the other bottom corner!
            return edgesToAvoid.includes(Edge.right) ? bottomLeftLoc : bottomRightLoc
        } else {
            // Use consistent toggle key so remix operations toggle from previous state
            const toggleKey = `randomBottomCornerLoc-${stateKey}`;
            const useLeft = RandomState.togglingRandomBool(toggleKey, randomState);
            return useLeft ? bottomLeftLoc : bottomRightLoc;
        }
    }

    // Returns a random corner loc from locs, optionally avoiding the given edgesToAvoid
    // Currently unused
    static randomTopCornerLoc(locs, edgesToAvoid = null, stateKey, randomState = null) {
        const topLeftLoc = BorderLoc.firstLocWithEdges(locs, [Edge.top, Edge.left])
        const topRightLoc = BorderLoc.firstLocWithEdges(locs, [Edge.top, Edge.right])

        if (edgesToAvoid) {
            // not really random, just return the other top corner!
            return edgesToAvoid.includes(Edge.right) ? topLeftLoc : topRightLoc
        } else {
            // Use consistent toggle key so remix operations toggle from previous state
            const toggleKey = `randomTopCornerLoc-${stateKey}`;
            const useLeft = RandomState.togglingRandomBool(toggleKey, randomState);
            return useLeft ? topLeftLoc : topRightLoc;
        }
    }

    static locsWithEdges(locs, edges) {
        return locs.filter(loc => {
            return arraysUnorderedEqual(loc.edges, edges)
        })
    }

    static firstLocWithEdges(locs, edges) {
        for (const loc of locs) {
            if (arraysUnorderedEqual(loc.edges, edges)) {
                return loc
            }
        }
        return null
    }
}

// TODO: move to utilities
function arraysUnorderedEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
        return false
    }

    let sortedArr1 = arr1.slice().sort()
    let sortedArr2 = arr2.slice().sort()

    for (let i = 0; i < sortedArr1.length; i++) {
        if (sortedArr1[i] !== sortedArr2[i]) {
            return false
        }
    }

    return true
}

// TODO: move to utilities
function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false
    return arr1.every((value, index) => value === arr2[index])
}

export { Edge, arraysUnorderedEqual, arraysEqual }